const toSet = values => new Set(values || [])

const getStoredValue = (storage, key) => (
    storage instanceof Map ? storage.get(key) : storage[key]
)

const isVisibleConditionSatisfied = (condition, context) => (
    condition === null || condition.evaluate(context)
)

const isDisabledConditionSatisfied = (condition, context) => (
    condition !== null && condition.evaluate(context)
)

const REVIEW_STATUSES = Object.freeze(['complete', 'attention', 'blocked', 'pending'])

const runtimeValidationChecks = (step, contribution) => {
    if (!step.hasValidationResult || step.isValid) {
        return []
    }

    const errors = Array.isArray(step.validationErrors) ? step.validationErrors : []

    if (errors.length > 0) {
        return errors.map((error, index) => ({
            id: `runtime-validation-${error.code || index}`,
            status: 'failed',
            severity: 'blocking',
            message: {
                key: error.messageKey || null,
                fallback: error.message || 'Complete this step before submitting.',
                params: error.params || {},
            },
        }))
    }

    const contributionChecks = Array.isArray(contribution.checks) ? contribution.checks : []
    if (contributionChecks.some(check => check.severity === 'blocking')) {
        return []
    }

    return [{
        id: 'runtime-validation-incomplete',
        status: 'failed',
        severity: 'blocking',
        message: {
            key: 'Page(ReceivedRequest):Component(WorkflowReview):complete-step-before-submit',
            fallback: 'Complete this step before submitting.',
            params: {},
        },
    }]
}

const normalizeReviewContribution = (step, contribution) => {
    if (!contribution || typeof contribution !== 'object') {
        return null
    }

    const contributionStatus = REVIEW_STATUSES.includes(contribution.status)
        ? contribution.status
        : 'attention'
    const status = step.hasValidationResult && !step.isValid
        ? 'blocked'
        : contributionStatus

    const checks = [...(contribution.checks || []), ...runtimeValidationChecks(step, contribution)]

    return Object.freeze({
        stepId: step.id,
        order: step.order,
        title: step.navigationTitle || step.title,
        icon: step.review.icon || contribution.icon || 'FileTextIcon',
        initiallyExpanded: step.review.initiallyExpanded === true,
        status,
        statusLabel: contribution.statusLabel || null,
        summary: contribution.summary || null,
        facts: Object.freeze([...(contribution.facts || [])]),
        checks: Object.freeze(checks),
    })
}

const getInitialOpenStepIds = (steps, accordion, initialStepMode) => {
    const reachableSteps = steps.filter(step => step.isReachable)
    const firstReachableStep = initialStepMode === 'resume'
        ? reachableSteps.find(step => !step.isCompleted)
            || reachableSteps[reachableSteps.length - 1]
        : reachableSteps[0]

    if (!firstReachableStep) {
        return new Set()
    }

    if (accordion) {
        return new Set([firstReachableStep.id])
    }

    return new Set([firstReachableStep.id])
}

const normalizeOpenStepIds = (requestedOpenStepIds, steps, accordion) => {
    const requested = toSet(requestedOpenStepIds)
    const openIds = steps
        .filter(step => step.isReachable && requested.has(step.id))
        .map(step => step.id)

    return new Set(accordion ? openIds.slice(0, 1) : openIds)
}

const createWorkflowRuntimeState = (compiledFlow, {
    context = {},
    completedStepIds = [],
    completedLegacyPersistedSteps = [],
    openStepIds,
    validityByStepId = {},
    dirtyStepIds = [],
    staleStepIds = [],
    busyStepIds = [],
    loadingStepIds = [],
    savingStepIds = [],
    errorsByStepId = {},
} = {}) => {
    const completedIds = toSet(completedStepIds)
    const completedPersistedSteps = toSet(completedLegacyPersistedSteps)
    const dirtyIds = toSet(dirtyStepIds)
    const staleIds = toSet(staleStepIds)
    const busyIds = toSet(busyStepIds)
    const loadingIds = toSet(loadingStepIds)
    const savingIds = toSet(savingStepIds)
    let visibleStepNumber = 0

    const availabilitySteps = compiledFlow.steps.map(step => {
        const isVisible = step.enabled && isVisibleConditionSatisfied(step.availability.visibleWhen, context)
        const isDisabled = isVisible && isDisabledConditionSatisfied(step.availability.disabledWhen, context)
        const isCompleted = completedIds.has(step.id)
            || (step.legacy !== null && completedPersistedSteps.has(step.legacy.persistedStep))
        const storedValidation = getStoredValue(validityByStepId, step.id)
        const hasValidationResult = storedValidation !== null
            && typeof storedValidation !== 'undefined'
        const validation = storedValidation || {
            isValid: step.validation.mode === 'none',
            errors: [],
        }
        let displayNumber = null

        if (isVisible && step.numbered && compiledFlow.settings.showStepNumbers) {
            visibleStepNumber += 1
            displayNumber = visibleStepNumber
        }

        return {
            ...step,
            isVisible,
            isDisabled,
            isCompleted,
            hasValidationResult,
            isValid: validation.isValid,
            validationErrors: validation.errors,
            isDirty: dirtyIds.has(step.id),
            isStale: staleIds.has(step.id),
            isBusy: busyIds.has(step.id),
            isLoading: loadingIds.has(step.id),
            isSaving: savingIds.has(step.id),
            error: getStoredValue(errorsByStepId, step.id) || null,
            displayNumber,
            isOpen: false,
            isMounted: false,
        }
    })
    let previousEnabledStep = null
    const initialSteps = availabilitySteps.map(step => {
        let isRevealed = false

        if (step.isVisible) {
            isRevealed = compiledFlow.settings.revealMode === 'all'
                || previousEnabledStep === null
                || step.isCompleted
                || previousEnabledStep.isCompleted

            if (!step.isDisabled) {
                previousEnabledStep = step
            }
        }

        return {
            ...step,
            isRevealed,
            isReachable: isRevealed && !step.isDisabled,
        }
    })
    const resolvedOpenStepIds = typeof openStepIds === 'undefined'
        ? getInitialOpenStepIds(
            initialSteps,
            compiledFlow.settings.accordion,
            compiledFlow.settings.initialStepMode,
        )
        : normalizeOpenStepIds(openStepIds, initialSteps, compiledFlow.settings.accordion)
    const steps = initialSteps.map(step => Object.freeze({
        ...step,
        isOpen: resolvedOpenStepIds.has(step.id),
        isMounted: step.isVisible && step.isRevealed && (
            step.mount.mode === 'eager'
            || step.isCompleted
            || resolvedOpenStepIds.has(step.id)
        ),
    }))
    const statesById = new Map(steps.map(step => [step.id, step]))
    const reviewContributions = steps
        .filter(step => (
            step.review.enabled
            && step.isVisible
            && typeof step.createReviewContribution === 'function'
        ))
        .map(step => normalizeReviewContribution(
            step,
            step.createReviewContribution({ context, step }),
        ))
        .filter(Boolean)
        .sort((left, right) => left.order - right.order)

    return Object.freeze({
        flow: compiledFlow,
        context,
        steps: Object.freeze(steps),
        reviewContributions: Object.freeze(reviewContributions),
        getStepState(id) {
            return statesById.get(id) || null
        },
    })
}

export { createWorkflowRuntimeState }
export default createWorkflowRuntimeState
